import { Container, Typography } from "@mui/material";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { pipe } from "fp-ts/function";
import { segmentDetailRoute } from "../../../../routes/org";
import { either, task, taskEither } from "fp-ts";
import { sequenceS } from "fp-ts/Apply";
import { errorsToMessage, pick, runTask } from "../../../../utils/fp";
import { executeSupabaseQuery } from "../../../../utils/supabase/useQuery";
import * as t from "io-ts";
import { column, columns } from "../../../../utils/pg-ts/pg-ts";
import { supabase } from "../../../../utils/supabase/init";
import { SegmentId, segmentTable } from "../../../../domain/event";
import * as React from "react";
import {
    SegmentsMapDialog,
    ShowSegmentsOnMapBtn,
    useSegmentsToPreviewState,
} from "../../../../components/org/event/ShowSegmentsOnMapBtn";
import { LocalDistance } from "../../../../components/common/LocalDistance";
import { Records } from "../../../../components/org/segment/Records";
import { HistoricalResults } from "../../../../components/org/segment/HistoricalResults";
import CvkLayout from "../../../../components/customOrgs/cvk/CvkLayout";

export const getServerSideProps = async (context: GetServerSidePropsContext) =>
    pipe(
        { ...context.query, subdomain: "cvk" },
        segmentDetailRoute.queryCodec.decode,
        either.mapLeft(errorsToMessage),
        taskEither.fromEither, // TODO learn the fp way to add context
        taskEither.chainW((query) =>
            sequenceS(taskEither.ApplyPar)({
                subdomain: taskEither.right(query.subdomain),
                segmentDetail: segmentDetailQuery(query.segmentId),
            })
        ),
        task.map(
            either.foldW(
                (e) => {
                    console.error(
                        "segment detail getServerSideProps error",
                        JSON.stringify(e)
                    );
                    return {
                        notFound: true as const,
                    };
                },
                (props) => {
                    return {
                        props,
                    };
                }
            )
        ),
        runTask
    );

export default function SegmentDetailPage(
    p: InferGetServerSidePropsType<typeof getServerSideProps>
) {
    const segmentsToPreviewState = useSegmentsToPreviewState();

    return (
        <CvkLayout subdomain={p.subdomain}>
            <Container maxWidth={"md"}>
                <Typography variant={"h4"} component={"h1"}>
                    Trať {p.segmentDetail.title}
                </Typography>
                <ul>
                    <li>
                        Vzdálenost:{" "}
                        <LocalDistance distance={p.segmentDetail.distance} />
                    </li>
                    <li>
                        <ShowSegmentsOnMapBtn
                            state={segmentsToPreviewState}
                            segments={[p.segmentDetail]}
                        />
                        <SegmentsMapDialog state={segmentsToPreviewState} />
                    </li>
                </ul>
                <Records segmentId={p.segmentDetail.id} />
                <Typography
                    component={"h2"}
                    variant={"h5"}
                    sx={{ mt: 4, mb: 1 }}
                >
                    Historický přehled všech zaznamenaných výkonů
                </Typography>

                <HistoricalResults segmentId={p.segmentDetail.id} />
            </Container>
        </CvkLayout>
    );
}

const segmentColumns = pipe(
    segmentTable.columns,
    pick("id", "title", "distance", "geojson")
);

const segmentDetailQuery = (segmentId: SegmentId) =>
    pipe(
        executeSupabaseQuery(
            `segmentDetail_${segmentId}`,
            t.type(segmentColumns),
            (supabase) =>
                supabase
                    .from(segmentTable.name)
                    .select(columns(segmentColumns))
                    .eq(column(segmentTable, "id"), segmentId)
                    .single(),
            supabase
        )
    );
